import { useAuth } from "contexts";
import { useEffect, useMemo, useState } from "react";

import { GameInterface } from "types/games";

import "./style.scss";
import { api } from "services";
import { getToken } from "utils";

export const GameFolder: any = {
	game_crossword: "crossword",
	game_wordsearch: "wordsearch",
	game_codex: "codex",
	game_enigma: "enigma",
	game_runner: "runner",
	game_detective: "detective",
	game_story: "story",
	game_embed: "embed",
	game_puzzle: "puzzle",
	game_memory: "memory",
};

const ACCESSIBLE_MODE_STORAGE_KEY = "game-accessible-mode";

function readAccessibleModePreference(): boolean {
	try {
		return localStorage.getItem(ACCESSIBLE_MODE_STORAGE_KEY) === "1";
	} catch (e) {
		return false;
	}
}

function saveAccessibleModePreference(enabled: boolean) {
	try {
		localStorage.setItem(ACCESSIBLE_MODE_STORAGE_KEY, enabled ? "1" : "0");
	} catch (e) {}
}

export type GameProps = {
	id: string;
	contentId?: string;
	className?: string;
	[key: string]: any;
};

export function GameContainer({ id, contentId, className }: GameProps) {
	const { user } = useAuth();
	const [gameURL, setGameUrl] = useState<any>();
	const [height, setHeight] = useState<number>();
	const [accessibleMode, setAccessibleMode] = useState(readAccessibleModePreference);

	const supportsAccessibleMode = GameFolder[id] === "crossword" || id === "crossword";

	function toggleAccessibleMode() {
		const next = !accessibleMode;
		setAccessibleMode(next);
		saveAccessibleModePreference(next);
	}

	const hasAspectRatio16vs9 = useMemo(() => {
		if (typeof id === "string" && !!id.match(/(game_story|phishing)/gi)) {
			return false;
		}

		return false;
	}, [id]);

	useEffect(() => {
		async function loadData() {
			if (!id) return;
			try {
				let service =
					!!(window as any).FUNIFIER_ENV?.service &&
					typeof (window as any).FUNIFIER_ENV?.service === "string"
						? (window as any).FUNIFIER_ENV?.service
						: process.env.REACT_APP_API_URL;

				service = service?.replace(/^(https?:\/\/)(.*)(\.funifier.*)$/gi, "$2");

				if (id === "embed") {
					const { data } = await api.post(`find/${id}`, {
						contentId: contentId,
					});

					if (data && data.length > 0) {
						console.log(data);
						if (data[0]?.url) {
							setGameUrl(
								`${
									data[0]?.url
								}?contentId=${contentId}&authorization=${getToken()}&service=${service}`,
							);
						}
					}
				} else {
					setGameUrl(
						`/game/${
							GameFolder[id] || id
						}/index.html?contentId=${contentId}&authorization=${getToken()}&service=${service}${
							supportsAccessibleMode && accessibleMode ? "&accessibility=1" : ""
						}`,
					);
				}
			} catch (e) {}
		}

		loadData();

		console.log("GameContainer", id, contentId);
	}, [id, contentId, accessibleMode, supportsAccessibleMode]);

	useEffect(() => {
		function receiveMessage(message: any) {
			if (message.data && typeof message.data === "string") {
				if (!!message.data.match(/height/gi)) {
					let _height = Number(message.data.replace(/[^.\d]/gi, ""));

					if (!isNaN(_height)) {
						_height = Math.round(_height);

						if (_height > 0) {
							setHeight(_height);
						} else {
							setHeight(undefined);
						}
					}
				}
			}
		}

		window.addEventListener("message", receiveMessage);

		return () => window.removeEventListener("message", receiveMessage);
	}, []);

	if (!gameURL) return null;

	return (
		<>
			{supportsAccessibleMode && (
				<button
					type="button"
					onClick={toggleAccessibleMode}
					aria-pressed={accessibleMode}
					className="mb-3 rounded-full border border-orange-500 bg-white px-4 py-2 text-sm font-bold text-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
				>
					{accessibleMode ? "Voltar à versão visual do jogo" : "Usar versão acessível (leitor de tela e teclado)"}
				</button>
			)}
			<div
				className={`game-container flex-1 rounded-xl lg:rounded-3xl overflow-hidden ${height ? 'custom-height' : ''}`}
				style={
					{
						"--height": `${height ? `${height}px` : "auto"}`,
					} as React.CSSProperties
				}
			>
				<iframe
					src={gameURL}
					title={accessibleMode && supportsAccessibleMode ? "Jogo de palavras cruzadas, versão acessível" : "Jogo"}
					className="w-full h-full"
				/>
			</div>
		</>
	);
}
